import { useState } from "react";
import { useTranslation } from "react-i18next";
import { reportOAuthError } from "../../firebase/oauthErrors";
import { signInWithGooglePopup } from "../../firebase/google.ts";
import { isEmbeddedBrowser } from "../../firebase/oauthEnv";
import { isNativeApp } from "../../native/platform";
import { InAppBrowserDialog } from "./auth/InAppBrowserDialog";
import { useAuthWithGoogle } from "../../hooks/useAuth.ts";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

const GoogleLoginButton = ({ compact = false }: { compact?: boolean }) => {
  const googleAuthMutation = useAuthWithGoogle();
  const { t } = useTranslation();
  // On the site this survives only until the redirect navigates away; in the
  // app it keeps a second tap from opening a second native sheet.
  const [isSigningIn, setIsSigningIn] = useState(false);
  const busy = isSigningIn || googleAuthMutation.isPending;
  const [showInAppHint, setShowInAppHint] = useState(false);

  const handleGoogleSignIn = async () => {
    if (busy) return;
    // Instagram/Facebook/… in-app browser: Google would show "access blocked".
    if (!isNativeApp() && isEmbeddedBrowser()) {
      setShowInAppHint(true);
      return;
    }
    setIsSigningIn(true);
    try {
      const { idToken } = await signInWithGooglePopup();

      // On web this returns null: the call above redirected away and the
      // token is handled on the next load by useCompleteOAuthRedirect().
      if (idToken) {
        googleAuthMutation.mutate(idToken);
      }
    } catch (error) {
      reportOAuthError("google", error);
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <>
      {showInAppHint && <InAppBrowserDialog onClose={() => setShowInAppHint(false)} />}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={busy}
        className="w-full h-11 auth-tall:h-12 flex items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-900/[.03] hover:bg-gray-50 hover:border-gray-300 hover:shadow focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 active:scale-[.99] disabled:opacity-60 disabled:cursor-default transition cursor-pointer"
      >
        {busy ? (
          <span className="auth-spinner w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600" />
        ) : (
          <GoogleIcon />
        )}
        <span className="text-[15px] font-semibold text-gray-800">{compact ? "Google" : t("authFlow.continueGoogle", "Продолжить с Google")}</span>
      </button>
    </>
  );
};

export default GoogleLoginButton;
